import * as angular from 'angular';
import * as _ from 'underscore';
import { NotificationService } from '../../../../../app.common/modules/common/services/notification.service';
import { OpeningHours } from '../../../core';
import { ShopService } from '../../shop.service';

interface IOpenTime {
    openAt: Date;
    closedAt: Date;
}

interface IOpeningErrors {
    date: string;
    openAt: string;
    closedAt: string;
    ranges: string;
}

interface IOpeningHours {
    index?: number;
    date?: Date;
    day?: number;
    dayOfWeek?: string;
    open24Hours: boolean;
    openingHours: IOpenTime[];
    closed: boolean;
    $$hashKey?: number;
    errors: IOpeningErrors;
}

export class ShopOpeningHoursController {
    public static $inject = ['$scope', '$filter', '$timeout', 'shopService', 'notificationService', '$translate'];

    public openingform: angular.xeditable.IEditableFormController;
    public normalOpeningHours: IOpeningHours[];
    public specialOpeningHours: IOpeningHours[];
    public dateOptions = {
        formatYear: 'yyyy',
        startingDay: 0
    };
    public shopID: number = null;

    private dateDefault = 'Thu, 01 Jan 1970 ';
    private serverData: OpeningHours = null;
    private timeout: number = 900;
    constructor(
        private $scope: ng.IScope,
        private $filter: ng.IFilterService,
        private $timeout: ng.ITimeoutService,
        private shopService: ShopService,
        private notificationService: NotificationService,
        private $translate: angular.translate.ITranslateService
    ) {
        if (navigator.userAgent.toLowerCase().indexOf('chrome') === -1) {
            this.timeout = 1800;
        }
    }

    public $onChanges(changes: any) {
        if (changes.shopId && changes.shopId.isFirstChange()) {
            this.shopID = changes.shopId.currentValue;
            this.getOpeningHours(this.shopID);
        }
    }

    public addInterval = (item) => {
        item.openingHours.push(this.getDefaultOpeningTime());
        this.openingform.$setDirty();
    }

    public deleteInterval = (item: IOpeningHours, opening: IOpenTime) => {
        item.openingHours = _.reject(item.openingHours, (openingHour) => {
            return openingHour.openAt === opening.openAt && openingHour.closedAt === opening.closedAt;
        });
    }

    public addSpecialOpening = () => {
        const opening = {
            date: new Date(),
            open24Hours: false,
            openingHours: this.setOpeningDateTime(null),
            closed: false,
            errors: {
                openAt: null,
                closedAt: null,
                date: null,
                ranges: null
            }
        };
        opening.date.setHours(0, 0, 0, 0);
        this.specialOpeningHours.push(opening);
        this.openingform.$setDirty();
    }

    public removeSpecialOpening = (opening: IOpeningHours) => {
        this.specialOpeningHours = _.reject(this.specialOpeningHours, (openingHour) => {
            return openingHour.$$hashKey === opening.$$hashKey;
        });
        this.openingform.$setDirty();
    }

    public beforeSaveOpeningHours = () => {
        this.clearErrors(this.specialOpeningHours);
        this.clearErrors(this.normalOpeningHours);

        this.validateDataBase();
        this.openingform.$invalid = this.isError(this.specialOpeningHours) || this.isError(this.normalOpeningHours);

        if (!this.openingform.$invalid) {
            this.validateTimeGreaterThan(this.specialOpeningHours);
            this.validateTimeGreaterThan(this.normalOpeningHours);
            this.openingform.$invalid = this.isError(this.specialOpeningHours) || this.isError(this.normalOpeningHours);
        }

        if (!this.openingform.$invalid) {
            this.validateRangesTime(this.specialOpeningHours);
            this.validateRangesTime(this.normalOpeningHours);
            this.validateUniqueDate();
            this.openingform.$invalid = this.isError(this.specialOpeningHours) || this.isError(this.normalOpeningHours);
        }

        if (!this.openingform.$invalid) {
            this.validateSpecialByNormal();
            this.openingform.$invalid = this.isError(this.specialOpeningHours) || this.isError(this.normalOpeningHours);
        }

        if (this.openingform.$invalid) {
            return 'invalid time';
        } else {
            this.saveOpeningHours();
            return 'something';
        }
    }

    public edit = () => {
        this.openingform.opacity = true;
        this.$timeout(this.removeOpacity, 200);
        this.openingform.$show();
        this.$scope.$emit('openOpeningHours', true);
    }

    public discard = () => {
        this.openingform.$cancel();
        this.openingform.$setPristine();
        if (this.serverData) {
            this.setClientModel(this.serverData);
        }
        this.$scope.$emit('openOpeningHours', false);
    }

    public hideOpeningHours = () => {
        this.$timeout(this.removeOpacity, this.timeout);
    }

    private getServerModel = () => {
        const normalOpeningHoursList = _.map(this.normalOpeningHours, (item) => {
            return {
                DayOfWeek: item.day,
                DayOfWeekString: item.dayOfWeek,
                Open24Hours: item.open24Hours,
                OpeningHours: this.setServerOpeningTime(item)
            };
        });

        const specialOpeningHoursList = _.map(this.specialOpeningHours, (item) => {
            return {
                Date: this.$filter('date')(item.date),
                Open24Hours: item.open24Hours,
                OpeningHours: this.setServerOpeningTime(item)
            };
        });

        return {
            SpecialOpeningHoursList: specialOpeningHoursList,
            NormalOpeningHoursList: normalOpeningHoursList
        };
    }

    private getTimeString = (time, isClosedAt?: boolean) => {
        const hours = time.getHours();
        const minutes = time.getMinutes();
        const seconds = time.getSeconds();
        if (isClosedAt && time.getTime() === this.getDateTime('23:59:59').getTime()) {
            return (hours < 10 ? '0' + hours : hours) + ':' +
                (minutes < 10 ? '0' + minutes : minutes) + ':' + seconds;
        }
        return (hours < 10 ? '0' + hours : hours) + ':' +
            (minutes < 10 ? '0' + minutes : minutes);
    }

    private setServerOpeningTime = (item: IOpeningHours) => {
        if (item.closed || item.open24Hours) {
            return [];
        }
        const startDate = this.getDateTime('00:00');

        return _.map(item.openingHours, (openingHour) => {
            return {
                OpenAt: this.getTimeString(openingHour.openAt),
                ClosedAt: startDate.getTime() === openingHour.closedAt.getTime() ?
                    this.getTimeString(this.getDateTime('23:59:59'), true)
                    : this.getTimeString(openingHour.closedAt)
            };
        });
    }

    private setClientModel = (data: OpeningHours) => {
        this.normalOpeningHours = _.map(data.NormalOpeningHoursList, (item) => {
            return {
                day: item.DayOfWeek,
                dayOfWeek: item.DayOfWeekString,
                open24Hours: item.Open24Hours,
                openingHours: this.setOpeningDateTime(item.OpeningHours),
                closed: !_.any(item.OpeningHours) && !item.Open24Hours,
                errors: {
                    openAt: null,
                    closedAt: null,
                    ranges: null,
                    date: null
                }
            };
        });

        this.specialOpeningHours = _.map(data.SpecialOpeningHoursList, (item) => {
            return {
                date: new Date(item.Date.toString()),
                open24Hours: item.Open24Hours,
                openingHours: this.setOpeningDateTime(item.OpeningHours),
                closed: !_.any(item.OpeningHours) && !item.Open24Hours,
                errors: {
                    openAt: null,
                    closedAt: null,
                    date: null,
                    ranges: null
                }
            };
        });
    }

    private setOpeningDateTime = (openingHours) => {
        const endOfDay = this.getDateTime('23:59:59');
        if (_.isArray(openingHours) && _.any(openingHours)) {
            return _.map(openingHours, (item) => {
                return {
                    openAt: this.getDateTime(item.OpenAt),
                    closedAt: endOfDay.getTime() === this.getDateTime(item.ClosedAt).getTime() ?
                        this.getDateTime('00:00') : this.getDateTime(item.ClosedAt)
                };
            });
        } else {
            return [this.getDefaultOpeningTime()];
        }
    }

    private getDateTime = (time) => {
        return new Date(Date.parse(this.dateDefault + (time ? time : '00:00')));
    }

    private getDefaultOpeningTime = () => {
        return {
            openAt: this.getDateTime('09:00'),
            closedAt: this.getDateTime('18:00')
        };
    }

    private clearErrors = (data) => {
        _.forEach(_.pluck(data, 'errors'), (object: IOpeningErrors) => {
            for (const key in object) {
                if (object.hasOwnProperty(key)) {
                    object[key] = null;
                }
            }
        });
    }

    private isError = (data) => {
        const errors = _.pluck(data, 'errors');
        return _.any(errors, (object: IOpeningErrors) => {
            for (const key in object) {
                if (object.hasOwnProperty(key) && _.isString(object[key])) {
                    return true;
                }
            }
            return false;
        });
    }

    private validateTime = (item) => {
        const invalidTime = this.$translate.instant('invalid_time');
        item.errors.openAt = _.any(_.pluck(item.openingHours, 'openAt'), (datetime: Date) => {
            return !_.isDate(datetime);
        }) ? invalidTime : null;

        item.errors.closedAt = _.any(_.pluck(item.openingHours, 'closedAt'), (datetime: Date) => {
            return !_.isDate(datetime);
        }) ? invalidTime : null;
    }

    private validateDataBase = () => {
        const invalidDate = this.$translate.instant('invalid_date');
        _.forEach(this.specialOpeningHours, (item) => {
            item.errors.date = _.isDate(item.date) ? null : invalidDate;
            if (!item.open24Hours && !item.closed) {
                this.validateTime(item);
            }
        });

        _.forEach(this.normalOpeningHours, (item) => {
            if (!item.open24Hours && !item.closed) {
                this.validateTime(item);
            }
        });
    }

    private validateUniqueDate = () => {
        const invalidDate = this.$translate.instant('date_must_be_unique');
        _.forEach(this.specialOpeningHours, (item) => {
            const date = new Date(item.date.toString()).setHours(0, 0, 0, 0);
            item.errors.date = _.any(this.specialOpeningHours, (opening) => {
                return opening.$$hashKey !== item.$$hashKey
                    && date === new Date(opening.date.toString()).setHours(0, 0, 0, 0);
            }) ? invalidDate : null;
        });
    }

    private validateRangesTime = (items: IOpeningHours[]) => {
        const invalidRanges = this.$translate.instant('ranges_musnt_intersect');
        _.forEach(items, (item) => {
            if (!item.open24Hours && !item.closed) {
                const openingHours = _.sortBy(item.openingHours, 'openAt');
                let previous = null;
                _.forEach(openingHours, (openingHour) => {
                    if (previous != null && (openingHour.openAt <= previous.openAt || openingHour.openAt <= previous.closedAt || openingHour.closedAt <= previous.closedAt)) {
                        item.errors.ranges = invalidRanges;
                    }
                    previous = openingHour;

                });
            }
        });
    }

    private validateTimeGreaterThan = (data: IOpeningHours[]) => {
        const invalidGreaterThan = this.$translate.instant('closing_time_error');
        _.forEach(data, (item) => {
            if (!item.open24Hours && !item.closed) {
                item.errors.ranges = _.any(item.openingHours, (interval) => {
                    const startDate = this.getDateTime('00:00');
                    if (startDate.getTime() === interval.closedAt.getTime()) {
                        return false;
                    }
                    return interval.openAt >= interval.closedAt;

                }) ? invalidGreaterThan : null;
            }

        });
    }

    private isEqualIntervals = (normal: IOpeningHours, special: IOpeningHours) => {
        if (normal.closed || special.closed) {
            return normal.closed && special.closed;
        }
        if (normal.open24Hours || special.open24Hours) {
            return normal.open24Hours && special.open24Hours;
        }
        if (_.size(normal.openingHours) !== _.size(special.openingHours)) {
            return false;
        }
        return _.all(normal.openingHours, (normalInterval) => {
            return _.any(special.openingHours, (specialInterval) => {
                const nOpen = new Date(normalInterval.openAt.toString()).setSeconds(0, 0);
                const nClosed = new Date(normalInterval.closedAt.toString()).setSeconds(0, 0);
                const sOpen = new Date(specialInterval.openAt.toString()).setSeconds(0, 0);
                const sClosed = new Date(specialInterval.closedAt.toString()).setSeconds(0, 0);
                return nOpen === sOpen && nClosed === sClosed;
            });
        });
    }

    private validateSpecialByNormal = () => {
        const equal = this.$translate.instant('special_hours_error');
        _.forEach(this.normalOpeningHours, (normal) => {
            const valid = true;
            _.forEach(this.specialOpeningHours, (special) => {
                if (normal.day === special.date.getDay() && this.isEqualIntervals(normal, special)) {
                    normal.errors.date = '*';
                    special.errors.date = equal;
                }
            });
        });
    }

    private getOpeningHours = async (id: number) => {
        if (this.openingform) {
            this.openingform.$waiting = true;
        }
        try {
            this.serverData = await this.shopService.getOpeningHours(id);
            this.setClientModel(this.serverData);
            this.notificationService.clearAll();

        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        if (this.openingform) {
            this.openingform.$waiting = false;
        }
    }

    private saveOpeningHours = async () => {
        if (this.openingform.$invalid) {
            return false;
        }

        this.notificationService.showWaitingMessage();

        this.openingform.$waiting = true;
        const model = this.getServerModel();

        try {
            await this.shopService.updateOpeningHours(this.shopID, model);
            this.$scope.$emit('openOpeningHours', false);
            this.getOpeningHours(this.shopID);
            this.openingform.$cancel();
            this.openingform.$setPristine();
            this.notificationService.showSuccessMessage();

        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.openingform.$waiting = false;
    }

    private removeOpacity = () => {
        this.openingform.opacity = false;
    }

}

angular.module('naut').component('shopOpeningHours', {
    template: require('./shopOpeningHours.html'),
    controller: ShopOpeningHoursController,
    controllerAs: '$ctrl',
    bindings: {
        shopId: '<',
    }
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/places/components/shopOpeningHours/shopOpeningHours.component.ts